import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_select.scss'

import addSubcomponents from '../utils/addSubcomponents'
import { DivAttributes, Props, PlacementNS, SizeNS, Omit } from '../utils/types'
import { OverlayTriggerVariantNS } from '../state/OverlayTrigger'
import Box from '../layouts/Box'
import { ButtonProps } from './Button'
import Card from './Card'
import Icon, { IconVariantNS } from './Icon'
import Notation, { NotationVariantNS } from './Notation'
import Menu, {
  Footer,
  Item,
  NavigationProps,
  MenuProps,
  Group,
  Search,
  Options,
} from './Menu'
import { OverlayTriggerAdapter as OverlayTrigger } from './OverlayTriggerAdapter'
import { DropdownPlacement } from './Dropdown'
import Spinner from './Spinner'

import { useSearch } from './Search'

type SelectPlacement = DropdownPlacement

interface OptionProps extends NavigationProps {
  value: any
  selected?: boolean
}

type ChangeEvent = React.ChangeEvent<HTMLInputElement>

interface SelectMenuProps extends Omit<MenuProps, 'scrollable'> {
  children?: React.ReactNode
  emptyMessage?: string
  footer?: React.ReactNode
  onSearch?: (event: ChangeEvent) => void
  refSearch?: React.RefObject<HTMLInputElement>
}

export interface SelectProps extends SelectMenuProps {
  block?: boolean
  disabled?: boolean
  error?: boolean
  label?: string
  loading?: boolean
  onClear?: (event: React.FormEvent<HTMLSpanElement>) => void
  placeholder?: string
  placement?: SelectPlacement
  onVisibleChange?: (isVisible: boolean, e: Event) => boolean
  tabIndex?: number
}

interface SelectButtonProps extends Omit<ButtonProps, 'variant'> {
  clearRef?: React.RefObject<HTMLSpanElement>
  error?: boolean
  label?: string
  onClear?: (event: React.FormEvent<HTMLSpanElement>) => void
  placeholder?: string
  tabIndex?: number
}

const cx = bind(styles)

const noop = () => {}

export const OptGroup = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => <Group {...props} clickable={false} ref={ref} />
)

export const Option = React.forwardRef<HTMLDivElement, OptionProps>(
  ({ value, ...props }, ref) => <Item ref={ref} {...props} item={value} />
)

const isOption = (obj: React.ReactChild) =>
  typeof obj === 'object' && obj.type === Option

const isTruthy = (attr: string) => (node: any) =>
  node && node.props && node.props[attr]
const isSelected = isTruthy('selected')
const isSuggested = isTruthy('suggested')

export const Button = React.forwardRef<
  HTMLDivElement,
  DivAttributes & SelectButtonProps
>(
  (
    {
      block = false,
      children,
      className,
      clearRef,
      disabled = false,
      error = false,
      label,
      loading = false,
      onClear,
      open = false,
      placeholder,
      tabIndex = 0,
      ...props
    },
    ref
  ) => {
    const buttonRef = ref || React.createRef<HTMLDivElement>()
    const hasClearIcon = onClear && !disabled && (!loading && label)
    const isDisabled = disabled || loading

    return (
      <div
        ref={buttonRef}
        tabIndex={isDisabled ? -1 : tabIndex}
        role="button"
        className={cx(
          'core-select',
          {
            ['core-select--block']: block,
            ['core-select--error']: error,
            ['core-select--disabled']: isDisabled,
            ['core-select--loading']: loading,
            ['core-select--open']: open,
          },
          className
        )}
        {...props}
      >
        <Box
          className={cx('core-select__label', {
            'core-select__label--placeholder': !label,
          })}
          tabIndex={-1}
        >
          {children || label || placeholder}
        </Box>
        {hasClearIcon && (
          <Icon
            clickable
            size={SizeNS.sm}
            ref={clearRef}
            icon="x"
            onClick={onClear}
            variant={IconVariantNS.hoverCircle}
            className={cx('core-select__clear-icon')}
          />
        )}
        {loading ? (
          <div className={cx('core-select__spinner')}>
            <Spinner size={SizeNS.xs} />
          </div>
        ) : (
          <div className={cx('core-select__arrow')} />
        )}
      </div>
    )
  }
)

const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      block = false,
      children,
      className,
      disabled = false,
      emptyMessage = 'No results',
      error = false,
      footer,
      isSelectable = isOption,
      label = '',
      loading = false,
      onClear,
      onSearch,
      onSelect = noop,
      onVisibleChange = (isVisible, e) => true,
      placeholder,
      placement = PlacementNS.bottomLeft,
      tabIndex = 0,
      ...props
    },
    ref
  ) => {
    const clearRef = React.createRef<HTMLSpanElement>()
    const refSearch = React.useRef(null)

    const search = useSearch({ onChange: onSearch, ref: refSearch })

    let initialIndex = 0

    if (search.empty) {
      const tempIndex = React.Children.toArray(children)
        .filter(node => isSelectable(node as React.ReactChild))
        .reduce(
          (acc, curr, index) => ({
            selected: isSelected(curr) ? index : acc.selected,
            suggested: isSuggested(curr) ? index : acc.suggested,
          }),
          { selected: -1, suggested: 0 } as any
        )

      initialIndex =
        tempIndex.selected >= 0 ? tempIndex.selected : tempIndex.suggested
    }

    return (
      <OverlayTrigger
        canFlip={true}
        autoFocus={!onSearch}
        container={Card}
        onVisibleChange={(isVisible, e) => {
          if (e.target === clearRef.current) {
            return false
          } else {
            const shouldToggle = onVisibleChange(isVisible, e) !== false
            if (shouldToggle && !isVisible) {
              search.clear()
            }
            return shouldToggle
          }
        }}
        containerClass={cx('core-select__overlay')}
        overlay={
          <Menu
            {...{
              ...props,
              initialIndex,
              isSelectable,
              onSelect,
              scrollable: true,
            }}
          >
            {onSearch && (
              <Search
                ref={refSearch}
                autoComplete="false"
                autoFocus
                empty={search.empty}
                onChange={search.onChange}
                showSearch={search.empty}
                onClear={search.clear}
                value={search.value}
              />
            )}
            {React.Children.count(children) ? (
              <Options>{children}</Options>
            ) : (
              <Box padding="md lg">
                <Notation variant={NotationVariantNS.pagination}>
                  {emptyMessage}
                </Notation>
              </Box>
            )}
            {footer && <Footer>{footer}</Footer>}
          </Menu>
        }
        placement={placement}
        ref={ref}
        trigger={OverlayTriggerVariantNS.click}
      >
        <Button
          {...props}
          block={block}
          className={className}
          clearRef={clearRef}
          disabled={disabled}
          error={error}
          label={label}
          loading={loading}
          placeholder={placeholder}
          onClear={onClear}
          ref={ref}
          tabIndex={tabIndex}
        />
      </OverlayTrigger>
    )
  }
)

Select.displayName = 'Select'

Button.displayName = 'Select.Button'

Option.displayName = 'Select.Option'

OptGroup.displayName = 'Select.OptGroup'

export default addSubcomponents(
  {
    Button,
    Option,
    OptGroup,
  },
  Select
)
